import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import { useAccount, useLogin } from '@bnnvara/accounts';
import { TrackEvent } from '@bnnvara/analytics';
import { useBrand } from '@bnnvara/brand';
import { LikeButton as StyledLikeButton } from '@bnnvara/buttons';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';

import { Like } from '../../api/mutations/like.gql';
import { IsLiked } from '../../api/queries/me/likes.gql';
import useLikes from '../../hooks/useLikes';
import LIKEQUERY from './enums';
import { StyledLikeButtonContainer } from './styled';
import { getLikes, increaseLikes } from './util';

const LikeButton = ({ id, type, slug }) => {
  const { login, isLoggedIn } = useLogin();
  const {
    profile: { UID },
  } = useAccount();
  const apolloClient = useApolloClient();
  const { slug: brandSlug } = useBrand();
  const { addToAllUserLikes } = useLikes();
  const [isLikedByUser, setIsLikedByUser] = useState();
  const typeLike = type === 'article' ? 'publication' : type;

  const handleClick = async (likeMutation) => {
    if (!isLoggedIn) return login();

    TrackEvent('like-button', 'click', `like-${type}`);

    // setIsLikedByUser(true);

    return likeMutation({
      variables: { interactionId: id, userId: UID, name: typeLike },
      optimisticResponse: {
        __typename: 'Mutation',
        like: true,
      },
    });
  };

  const variables = {
    media: {
      brandSlug,
      id,
    },
    article: {
      brandSlug,
      slug,
    },
  };

  const { loading, error, data } = useQuery(LIKEQUERY[type], {
    variables: variables[type],
    pollInterval: 3600000,
  });

  const [likeContent] = useMutation(Like, {
    onError: () => {
      setIsLikedByUser((isLiked) => !isLiked);
    },
    update: (cache, { data: { like } }) => {
      const cachedData = cache.readQuery({
        query: LIKEQUERY[type],
        variables: variables[type],
      });

      // Storing the amount of likes of an article in an cache to assure that if you go back and forward between a page that updated amount of likes is still visible
      cache.writeQuery({
        query: LIKEQUERY[type],
        variables: variables[type],
        data: increaseLikes(type, cachedData, isLikedByUser),
      });

      addToAllUserLikes(id, type);
      setIsLikedByUser(like);
    },
  });

  useEffect(() => {
    const fetchAndSetFollow = async () => {
      const { data: likes } = await apolloClient.query({
        query: IsLiked,
        variables: {
          interactionId: id,
          userId: UID,
          name: typeLike,
        },
        fetchPolicy: 'network-only',
        options: {
          pollInterval: 3600000,
        },
      });
      setIsLikedByUser(likes.me.isLiked);
    };

    // default value, but necessary to update state if user logs out during session.
    if (!isLoggedIn) {
      setIsLikedByUser(false);
    }

    if (isLoggedIn) {
      fetchAndSetFollow();
    }
  }, [apolloClient, isLoggedIn, id, UID, typeLike]);

  // This check prevents that the Apollo query will enter an infinite loop if the type isn't correct
  if (type !== 'media' && type !== 'article') {
    return null;
  }

  if (loading || error) return null;

  return (
    <Fragment>
      <StyledLikeButtonContainer>
        <StyledLikeButton
          disabled={isLikedByUser}
          onClick={() => handleClick(likeContent)}
          isLikedByUser={isLikedByUser}
          likes={getLikes(type, data)}
        />
      </StyledLikeButtonContainer>
    </Fragment>
  );
};

LikeButton.propTypes = {
  type: PropTypes.oneOf(['media', 'article']).isRequired,
  slug: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

LikeButton.defaultProps = {
  slug: null,
};

export default LikeButton;
